import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Globe,
  MousePointerClick,
  Eye,
  TrendingUp,
  RefreshCw,
  Plus,
  Loader2,
  FileText,
  BarChart3,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  CheckCircle,
  Calendar,
  ExternalLink,
  Link2
} from 'lucide-react';
import api, {
  GSCDashboard,
  GSCSitemap,
  GSCAnalytics
} from '../services/api';
import { useClient } from '../contexts/ClientContext';

type TabType = 'overview' | 'queries' | 'pages' | 'performance' | 'sitemaps';

const SEO = ({ embedded = false }: { embedded?: boolean }) => {
  const { selectedClientId } = useClient();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<GSCDashboard | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [syncing, setSyncing] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<number>(28);

  // Data states
  const [analytics, setAnalytics] = useState<GSCAnalytics | null>(null);
  const [sitemaps, setSitemaps] = useState<GSCSitemap[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Load dashboard
  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getGSCDashboard(selectedClientId || undefined);
      setDashboard(data);

      // Auto-select first property if only one exists
      if (data.properties.length === 1 && selectedProperty === 'all') {
        setSelectedProperty(data.properties[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load SEO dashboard');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Load tab-specific data
  useEffect(() => {
    if (selectedProperty === 'all') return;

    if (activeTab === 'queries' || activeTab === 'pages' || activeTab === 'performance') {
      loadAnalytics();
    } else if (activeTab === 'sitemaps') {
      loadSitemaps();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedProperty, dateRange]);

  const loadAnalytics = async () => {
    if (selectedProperty === 'all') return;
    try {
      setDataLoading(true);
      const response = await api.getGSCPropertyAnalytics(selectedProperty, dateRange);
      setAnalytics(response);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setDataLoading(false);
    }
  };

  const loadSitemaps = async () => {
    if (selectedProperty === 'all') return;
    try {
      setDataLoading(true);
      const response = await api.getGSCSitemaps(selectedProperty);
      setSitemaps(response.sitemaps || []);
    } catch (err) {
      console.error('Failed to load sitemaps:', err);
    } finally {
      setDataLoading(false);
    }
  };

  const handleConnectProperty = async () => {
    try {
      const { auth_url } = await api.getGSCAuthUrl();
      window.location.href = auth_url;
    } catch (err) {
      console.error('Failed to get auth URL:', err);
      setError('Failed to initiate Google Search Console connection');
    }
  };

  const handleSync = async (propertyId: string) => {
    try {
      setSyncing(propertyId);
      await api.syncGSCProperty(propertyId, dateRange);
      await loadDashboard();

      // Reload current tab data
      if (selectedProperty === propertyId) {
        if (activeTab === 'queries' || activeTab === 'pages' || activeTab === 'performance') {
          loadAnalytics();
        } else if (activeTab === 'sitemaps') {
          loadSitemaps();
        }
      }
    } catch (err) {
      console.error('Failed to sync property:', err);
      setError('Failed to sync property');
    } finally {
      setSyncing(null);
    }
  };

  // Formatting utilities
  const formatNumber = (value: number | string | null | undefined) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return (num || 0).toLocaleString();
  };

  const formatPercent = (value: number | string | null | undefined) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `${((num || 0) * 100).toFixed(2)}%`;
  };

  const formatPosition = (value: number | string | null | undefined) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return (num || 0).toFixed(1);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filter queries by search term
  const filteredQueries = analytics?.top_queries?.filter(q =>
    !searchTerm || q.query.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Filter pages by search term
  const filteredPages = analytics?.top_pages?.filter(p =>
    !searchTerm || p.page_url.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const needsPropertySelection = ['queries', 'pages', 'performance', 'sitemaps'].includes(activeTab) && selectedProperty === 'all';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "min-h-screen bg-gray-100 dark:bg-black"}>
      {!embedded && (
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-base font-bold text-gray-900 dark:text-white">SEO - Search Console</h1>
              <p className="text-gray-600 dark:text-gray-300">
                Google Search Console performance tracking
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Property Selector */}
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 min-w-[200px]"
              >
                <option value="all">All Properties</option>
                {dashboard?.properties.map((prop) => (
                  <option key={prop.id} value={prop.id}>
                    {prop.display_name || prop.site_url}
                  </option>
                ))}
              </select>

              {/* Date Range */}
              <select
                value={dateRange}
                onChange={(e) => setDateRange(Number(e.target.value))}
                className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600"
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={28}>Last 28 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
              </select>

              <button
                onClick={loadDashboard}
                disabled={loading}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>


              <button
                onClick={handleConnectProperty}
                className="flex items-center gap-2 bg-primary-500 text-black px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Connect Property
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      <div className={embedded ? "" : "max-w-7xl mx-auto px-3 py-3"}>
        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-2 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-sm text-red-700 hover:text-red-900 underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-2">
          {(['overview', 'queries', 'pages', 'performance', 'sitemaps'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-primary-500 text-black'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {tab === 'queries' ? 'Search Queries' : tab === 'sitemaps' ? 'Sitemaps' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Property Selection Required Message */}
        {needsPropertySelection && (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
            <Globe className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Select a Property
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Please select a specific property from the dropdown above to view detailed data.
            </p>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-3">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <Globe className="w-5 h-5 text-blue-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {dashboard?.total_properties || 0}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Properties</p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <MousePointerClick className="w-5 h-5 text-green-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatNumber(dashboard?.stats?.total_clicks)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Total Clicks</p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <Eye className="w-5 h-5 text-purple-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatNumber(dashboard?.stats?.total_impressions)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Impressions</p>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {formatPosition(dashboard?.stats?.average_position)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">Avg Position</p>
              </div>
            </div>

            {/* Connected Properties */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Connected Properties</h2>
              </div>
              <div className="p-4">
                {dashboard?.properties && dashboard.properties.length > 0 ? (
                  <div className="space-y-3">
                    {dashboard.properties.map((property) => (
                      <div
                        key={property.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${property.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {property.display_name || property.site_url}
                            </h4>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {property.site_url}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatNumber(property.total_clicks)} clicks
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatNumber(property.total_impressions)} impressions
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              Pos: {formatPosition(property.average_position)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              CTR: {formatPercent(property.average_ctr)}
                            </p>
                          </div>
                          <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {formatDate(property.last_synced_at)}
                          </div>
                          <button
                            onClick={() => handleSync(property.id)}
                            disabled={syncing === property.id}
                            className="flex items-center gap-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                            title="Sync data"
                          >
                            {syncing === property.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={handleConnectProperty}
                            className="flex items-center gap-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                            title="Reconnect (re-authorize with Google)"
                          >
                            <Link2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Globe className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400 mb-4">No Google Search Console properties connected</p>
                    <button
                      onClick={handleConnectProperty}
                      className="flex items-center gap-2 bg-primary-500 text-black px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors mx-auto"
                    >
                      <Plus className="w-5 h-5" />
                      Connect Property
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Queries Tab */}
        {activeTab === 'queries' && !needsPropertySelection && (
          <div className="space-y-3">
            {/* Search */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search queries..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600"
                />
              </div>
            </div>

            {/* Queries Table */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Query</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Clicks</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Impressions</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CTR</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Position</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {dataLoading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary-500" />
                        </td>
                      </tr>
                    ) : filteredQueries.length > 0 ? (
                      filteredQueries.map((query, idx) => (
                        <tr key={query.id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-3">
                            <span className="font-medium text-gray-900 dark:text-white">{query.query}</span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900 dark:text-white">{formatNumber(query.clicks)}</td>
                          <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{formatNumber(query.impressions)}</td>
                          <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{formatPercent(query.ctr)}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-flex items-center gap-1 ${
                              parseFloat(String(query.position)) <= 10 ? 'text-green-600' :
                              parseFloat(String(query.position)) <= 20 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {parseFloat(String(query.position)) <= 10 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                              {formatPosition(query.position)}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                          No queries found. Try syncing the property first.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Pages Tab */}
        {activeTab === 'pages' && !needsPropertySelection && (
          <div className="space-y-3">
            {/* Search */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search pages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600"
                />
              </div>
            </div>

            {/* Pages Table */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Page</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Clicks</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Impressions</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CTR</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Position</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {dataLoading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary-500" />
                        </td>
                      </tr>
                    ) : filteredPages.length > 0 ? (
                      filteredPages.map((page, idx) => (
                        <tr key={page.id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-3">
                            <a
                              href={page.page_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-primary-500 hover:text-primary-600"
                            >
                              <span className="truncate max-w-md">{page.page_url}</span>
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900 dark:text-white">{formatNumber(page.clicks)}</td>
                          <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{formatNumber(page.impressions)}</td>
                          <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{formatPercent(page.ctr)}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-flex items-center gap-1 ${
                              parseFloat(String(page.position)) <= 10 ? 'text-green-600' :
                              parseFloat(String(page.position)) <= 20 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {formatPosition(page.position)}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                          No pages found. Try syncing the property first.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && !needsPropertySelection && (
          <div className="space-y-3">
            {/* Performance Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                    <MousePointerClick className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatNumber(analytics?.summary?.total_clicks)}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">Total Clicks</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <Eye className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatNumber(analytics?.summary?.total_impressions)}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">Impressions</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatPercent(analytics?.summary?.average_ctr)}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">Avg CTR</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatPosition(analytics?.summary?.average_position)}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300">Avg Position</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Daily Data Table */}
            <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Daily Performance</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Clicks</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Impressions</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">CTR</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Position</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {dataLoading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary-500" />
                        </td>
                      </tr>
                    ) : analytics?.daily_data && analytics.daily_data.length > 0 ? (
                      [...analytics.daily_data].reverse().map((day, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <td className="px-4 py-3 text-gray-900 dark:text-white">
                            {new Date(day.date).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900 dark:text-white">{formatNumber(day.clicks)}</td>
                          <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{formatNumber(day.impressions)}</td>
                          <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{formatPercent(day.ctr)}</td>
                          <td className="px-4 py-3 text-right text-gray-500 dark:text-gray-400">{formatPosition(day.position)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                          No performance data available. Try syncing the property first.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Sitemaps Tab */}
        {activeTab === 'sitemaps' && !needsPropertySelection && (
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Submitted Sitemaps</h2>
            </div>
            <div className="p-4">
              {dataLoading ? (
                <div className="py-8 text-center">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary-500" />
                </div>
              ) : sitemaps.length > 0 ? (
                <div className="space-y-3">
                  {sitemaps.map((sitemap) => (
                    <div
                      key={sitemap.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          sitemap.errors > 0 ? 'bg-red-100 dark:bg-red-900/30' :
                          sitemap.warnings > 0 ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                          'bg-green-100 dark:bg-green-900/30'
                        }`}>
                          {sitemap.errors > 0 ? (
                            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                          ) : sitemap.warnings > 0 ? (
                            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                          ) : (
                            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                          )}
                        </div>
                        <div>
                          <a
                            href={sitemap.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 font-medium text-primary-500 hover:text-primary-600"
                          >
                            {sitemap.path}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Type: {sitemap.sitemap_type || 'sitemap'}
                            {sitemap.is_sitemaps_index && ' (Index)'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        {sitemap.errors > 0 && (
                          <span className="text-red-600 dark:text-red-400">
                            {sitemap.errors} errors
                          </span>
                        )}
                        {sitemap.warnings > 0 && (
                          <span className="text-yellow-600 dark:text-yellow-400">
                            {sitemap.warnings} warnings
                          </span>
                        )}
                        <span className="text-gray-500 dark:text-gray-400">
                          Submitted: {formatDate(sitemap.last_submitted)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">
                    No sitemaps found. Sync the property to fetch sitemap data.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SEO;
